import React, { useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, ExternalLink, ChevronDown, MapPin, Clock, Thermometer, Users, Globe, Navigation, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEO, { breadcrumbSchema } from '../components/SEO';

/* ─── Lightbox ─── */
const Lightbox = ({ src, alt, onClose }) => {
  /* Lock body scroll on mount, restore on unmount */
  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = '';
    };
  }, []);

  /* Keyboard navigation */
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, backgroundColor: "rgba(0,0,0,0.95)" }} onClick={onClose}>
      
      <div style={{ position: "relative", display: "inline-flex", maxWidth: "90vw", maxHeight: "90vh" }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{ position: "absolute", top: 8, right: 8, width: 34, height: 34, backgroundColor: "rgba(50,50,50,0.92)", border: "none", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 20 }}><X size={15} style={{ color: "#fff" }} /></button>
        <img src={src} alt={alt} style={{ maxHeight: "90vh", maxWidth: "90vw", objectFit: "contain", borderRadius: 12 }} />
      </div>
    </div>,
    document.body
  );
};

/* ─── Clickable image ─── */
const ZoomImg = ({ src, alt, className }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div className={`relative group cursor-zoom-in overflow-hidden ${className}`} onClick={() => setOpen(true)}>
        <img src={src} alt={alt} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all" />
        <div className="absolute bottom-2 right-2 w-7 h-7 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
        </div>
      </div>
      {open && <Lightbox src={src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
};

/* ═══════════════════════════════════════════════
   EXPEDITION 1 — MAGNETIC NORTH POLE 2023
═══════════════════════════════════════════════ */
const NorthPole2023 = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  return (
  <div>
    {/* Hero */}
    <div className="relative overflow-hidden" style={{ height: "calc(100vh - 300px)" }}>
      <img
        src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/e70o39w2_image.png"
        alt="Arctic expedition camp at dusk — Arctic Trucks AT44 with Total Composites habitat"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.88) 100%)' }} />
      <div className="relative z-10 flex flex-col justify-end h-full pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.2em' }}>
            TOTAL COMPOSITES × Arctic Trucks — {fr ? 'Avril 2023' : 'April 2023'}
          </p>
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-none mb-5 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? <>Conçu pour<br /><span style={{ color: '#E8520A' }}>là où la</span><br />route s'arrête</> : <>Built for<br /><span style={{ color: '#E8520A' }}>Where the</span><br />Road Ends</>}
          </h2>
          <p className="text-gray-300 text-lg max-w-xl leading-relaxed">
            {fr ? 'De Victoria, C.-B. au Pôle Nord magnétique — 24 jours, -39 °C, et un habitat qui n\'a jamais failli.' : 'From Victoria, BC to the Magnetic North Pole — 24 days, -39°C, and a habitat that never failed.'}
          </p>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <ChevronDown size={28} className="text-white opacity-60" />
      </div>
    </div>

    {/* Stats */}
    <div className="bg-black py-10">
      <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 gap-8">
        {[
          { icon: Clock, value: fr ? '24 JOURS' : '24 DAYS', label: fr ? 'De Yellowknife au Pôle Nord magnétique et retour' : 'Yellowknife to Magnetic North Pole and back' },
          { icon: Thermometer, value: '-39°C', label: fr ? 'Température minimale enregistrée' : 'Minimum temperature recorded' },
          { icon: Users, value: fr ? '7 PERSONNES' : '7 PEOPLE', label: fr ? 'Membres d\'équipe abrités par l\'habitat' : 'Team members sheltered by the habitat' },
          { icon: MapPin, value: fr ? 'AVRIL 2023' : 'APRIL 2023', label: fr ? 'Pôle Nord magnétique atteint' : 'Magnetic North Pole reached' },
        ].map(({ icon: Icon, value, label }, i) => (
          <div key={label} className="text-center">
            <Icon size={16} style={{ color: '#E8520A' }} className="mx-auto mb-2" />
            <div className="text-2xl font-black text-white mb-1">{value}</div>
            <div className="text-xs text-gray-500 leading-relaxed">{label}</div>
          </div>
        ))}
      </div>
    </div>

    {/* Mission */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>{fr ? 'Expédition réelle. Conditions réelles.' : 'Real Expedition. Real Conditions.'}</p>
            <h3 className="text-4xl font-black text-gray-900 mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
              {fr ? <>Pôle Nord magnétique.<br />Avril 2023.</> : <>Magnetic North Pole.<br />April 2023.</>}
            </h3>
            <div className="space-y-4 text-gray-600 text-sm leading-relaxed mb-8">
              {fr ? (
                <>
                  <p>En soutien à l&apos;<strong className="text-gray-900">Expédition Pole to Pole EV</strong> — le tout premier voyage en véhicule électrique vers le Pôle Nord magnétique — Arctic Trucks a monté un <strong className="text-gray-900">campeur TOTAL COMPOSITES Bobcat 8' sur plateau</strong> sur leur conversion AT44 Ford F-350 6×6.</p>
                  <p>Trois véhicules et sept personnes sont partis de Yellowknife, T.N.-O. 24 jours à travers l&apos;océan gelé, la neige profonde et des milliers de kilomètres d&apos;isolement total. L&apos;habitat a servi d&apos;unique abri à l&apos;équipe par des températures atteignant -39 °C.</p>
                  <p className="font-bold text-gray-900">L&apos;équipe l&apos;a surnommé « le club house ». Non pas parce qu&apos;il était confortable — mais parce qu&apos;il fonctionnait.</p>
                </>
              ) : (
                <>
                  <p>In support of the <strong className="text-gray-900">Pole to Pole EV Expedition</strong> — the first-ever electric vehicle journey to the magnetic North Pole — Arctic Trucks mounted a <strong className="text-gray-900">TOTAL COMPOSITES Bobcat 8' flatbed camper</strong> to their AT44 Ford F-350 6×6 conversion.</p>
                  <p>Three vehicles and seven people set out from Yellowknife, NWT. 24 days across frozen ocean, deep snow, and thousands of miles of total isolation. The habitat served as the team's only shelter in temperatures reaching -39°C.</p>
                  <p className="font-bold text-gray-900">The team called it "the clubhouse." Not because it was comfortable — but because it worked.</p>
                </>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: fr ? 'Départ' : 'Start', value: 'Yellowknife, NWT' },
                { label: fr ? 'Durée' : 'Duration', value: fr ? '24 jours' : '24 Days' },
                { label: fr ? 'Véhicule' : 'Vehicle', value: 'AT44 F-350 6×6' },
                { label: fr ? 'Modèle' : 'Model', value: 'TC Bobcat 8\'' },
              ].map((s, i) => (
                <div key={s.label} className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-400 uppercase tracking-widest font-bold mb-0.5">{s.label}</p>
                  <p className="text-sm font-bold text-gray-900">{s.value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <ZoomImg
              src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/b16ffjyd_image.png"
              alt="Two Arctic Trucks AT44 6x6 with Total Composites habitat on Arctic snow"
              className="rounded-2xl h-64 shadow-xl"
            />
            <ZoomImg
              src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/8usu29pk_image.png"
              alt="Chris and Julie Ramsey — Pole to Pole EV expedition at the South Pole"
              className="rounded-2xl h-64 shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Quote */}
    <section className="py-20 bg-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>
          Kyle Leeds — {fr ? 'Directeur des ventes, Arctic Trucks' : 'Arctic Trucks Director of Sales'}
        </p>
        <blockquote className="text-xl lg:text-2xl font-light text-white leading-relaxed italic mb-8">
          {fr
            ? "« TOTAL COMPOSITES a été sélectionné en raison de la conception supérieure de l'isolation, de la qualité et de la durabilité. La construction composite sans transfert thermique était vitale pour éviter la condensation et l'accumulation de glace à des températures extrêmement basses. Pour TOTAL COMPOSITES, c'était une occasion unique de prouver leur promesse de durabilité, d'efficacité et de conductivité thermique nulle. »"
            : '"TOTAL COMPOSITES was selected due to the superior insulation design, quality, and durability. The thermal-free composite construction was vital to avoid condensation and ice build-up in extreme low temperatures. For TOTAL COMPOSITES this was a huge opportunity to prove their claim about the durability, efficiency and zero thermal conductivity."'}
        </blockquote>
        <a
          href="https://www.truckcamperadventure.com/arctic-trucks-at44-total-composites-camper-tackles-epic-trip-to-magnetic-north-pole/"
          target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-xs font-semibold transition-colors hover:opacity-80"
          style={{ color: '#E8520A' }}
        >
          {fr ? 'LIRE L\'ARTICLE COMPLET — TRUCK CAMPER ADVENTURE' : 'READ FULL ARTICLE — TRUCK CAMPER ADVENTURE'} <ExternalLink size={11} />
        </a>
      </div>
    </section>

    {/* Why Composites */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>{fr ? 'Réalité d\'ingénierie' : 'Engineering Reality'}</p>
          <h3 className="text-4xl font-black text-gray-900 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>{fr ? <>Pourquoi les composites<br />comptent dans l'Arctique</> : <>Why Composites<br />Matter in the Arctic</>}</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            <h4 className="font-black text-gray-400 uppercase text-sm tracking-widest mb-5">{fr ? 'Les constructions traditionnelles échouent' : 'Traditional Builds Fail'}</h4>
            <ul className="space-y-3">
              {(fr
                ? ['La condensation se transforme en glace dans les murs', 'Les matériaux se contractent, se fissurent et se séparent', 'Le bois absorbe l\'humidité, se déforme, pourrit', 'Le métal conduit le froid, crée des ponts thermiques', 'L\'humidité engendre de la moisissure', 'Le froid devient une défaillance structurelle']
                : ['Condensation turns to ice inside walls', 'Materials contract, crack, and separate', 'Wood absorbs moisture, warps, rots', 'Metal conducts cold, creates frost bridges', 'Moisture becomes mold', 'Cold becomes a structural failure']
              ).map((item, i) => (
                <li key={item} className="flex items-start gap-3 text-sm text-gray-500">
                  <span className="text-red-400 font-bold flex-shrink-0">✗</span>{item}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white rounded-2xl p-8 border-2" style={{ borderColor: '#E8520A' }}>
            <h4 className="font-black uppercase text-sm tracking-widest mb-5" style={{ color: '#E8520A' }}>{fr ? 'TOTAL COMPOSITES résout le problème' : 'TOTAL COMPOSITES Solves It'}</h4>
            <ul className="space-y-3">
              {(fr
                ? ['Le composite sans transfert thermique empêche la condensation et la glace', 'L\'isolation haute efficacité garde les intérieurs secs', 'Les panneaux légers réduisent le stress sur le véhicule', 'La résistance monocoque résiste aux charges extrêmes', '100 % sans bois — zéro pourriture, zéro absorption d\'humidité', 'Prouvé : choisi parce que l\'échec signifiait un danger réel']
                : ['Thermal-free composite prevents condensation and ice', 'High-efficiency insulation keeps interiors dry', 'Lightweight panels reduce vehicle stress', 'Monocoque strength resists extreme loads', '100% wood-free — zero rot, zero moisture absorption', 'Proven: chosen because failure meant real danger']
              ).map((item, i) => (
                <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="font-bold flex-shrink-0" style={{ color: '#E8520A' }}>✓</span>{item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* In The Field Gallery */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>{fr ? 'Sur le terrain' : 'In The Field'}</p>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Video */}
          <div>
            <div className="rounded-2xl overflow-hidden shadow-xl bg-black" style={{ aspectRatio: '9/16' }}>
              <iframe
                src="https://www.youtube.com/embed/LwhVMloRvvY"
                title="Magnetic North Pole Expedition — TOTAL COMPOSITES"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          {/* Photos */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/tnclih00_989E790E-FEDC-4A09-A709-BC85C7624177_1_105_c.jpeg" alt="Arctic Trucks AT44 with Total Composites habitat on cracked Arctic ice" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/iu15i61k_F96D978D-1548-4DDA-83DF-A340C95092FE-scaled.jpeg" alt="Total Composites habitat under Northern Lights" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/64cjbgg2_9C4E9990-BC8F-460E-9DC4-CA25BF9F9738-scaled.jpeg" alt="Arctic Trucks AT44 traversing snowy terrain" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/jw1prpqd_330D8A9A-1825-4D71-A338-578C752A26DA-scaled.jpeg" alt="Aerial view of two AT44 trucks crossing Arctic ridge" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/fa46c1o4_Screenshot%202026-03-27%20161617.png" alt="Arctic Trucks AT44 convoy with Total Composites habitat on Arctic ice ridge" className="rounded-2xl col-span-2 shadow-xl h-52" />
          </div>
        </div>
      </div>
    </section>

    {/* Designed for the Unknown / Permanent Legacy */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>{fr ? 'Conçu pour l\'inconnu' : 'Designed for the Unknown'}</p>
          <h3 className="text-4xl font-black text-gray-900 mb-6 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? <>De l'île de Vancouver<br /><span style={{ color: '#E8520A' }}>au sommet du monde.</span></> : <>From Vancouver Island<br /><span style={{ color: '#E8520A' }}>to the Top of the World.</span></>}
          </h3>
          <div className="space-y-4 text-gray-500 text-sm leading-relaxed">
            {fr ? (
              <>
                <p>TOTAL COMPOSITES est basée à Victoria, en Colombie-Britannique — au bord du Pacifique, entourée de nature sauvage. Mais nos produits ne restent pas près de chez nous.</p>
                <p>Ils ont traversé la banquise arctique, le nord isolé du Canada, dans des environnements où aucun VR conventionnel ne survivrait — non pas comme véhicules de soutien, mais comme systèmes essentiels à la mission.</p>
                <p>Avant TOTAL COMPOSITES, il n&apos;existait pratiquement aucun campeur porté capable de fonctionner à ces températures. Les expéditions arctiques signifiaient des tentes. Cette mission a changé cela de façon permanente — prouvant que la construction composite pouvait abriter une équipe dans l&apos;environnement le plus extrême de la Terre.</p>
                <p className="font-bold text-gray-900">C&apos;est la différence entre un campeur… et un habitat.</p>
              </>
            ) : (
              <>
                <p>TOTAL COMPOSITES is based in Victoria, British Columbia — on the edge of the Pacific, surrounded by wilderness. But our products don't stay close to home.</p>
                <p>They've traveled across frozen Arctic sea ice, through remote northern Canada, into environments where no conventional RV would survive — not as support vehicles, but as mission-critical systems.</p>
                <p>Before TOTAL COMPOSITES, there were essentially no camper options capable of operating at these temperatures. Arctic expeditions meant tents. This mission changed that permanently — proving that composite construction could shelter a team through the most extreme environment on Earth.</p>
                <p className="font-bold text-gray-900">That's the difference between a camper… and a habitat.</p>
              </>
            )}
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: Globe, title: fr ? 'Première mondiale' : 'World First', desc: fr ? 'Premier système de campeur porté jamais utilisé en soutien d\'une expédition véhiculaire au Pôle Nord magnétique' : 'First truck camper system ever used in support of a magnetic North Pole vehicle expedition' },
            { icon: Navigation, title: fr ? 'Éprouvé en Arctique' : 'Arctic Proven', desc: fr ? 'Les habitats TOTAL COMPOSITES ont été choisis spécifiquement parce que les constructions conventionnelles auraient échoué dans ces conditions' : 'TOTAL COMPOSITES habitats were chosen specifically because conventional builds would have failed in those conditions' },
            { icon: MapPin, title: fr ? 'Une nouvelle norme' : 'A New Standard', desc: fr ? 'Cette expédition a établi une nouvelle référence — prouvant que les habitats composites sont le seul choix viable pour les opérations polaires extrêmes' : 'This expedition set a new benchmark — proving composite habitats are the only viable choice for extreme polar operations' },
          ].map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#FFF0E8' }}>
                <Icon size={18} style={{ color: '#E8520A' }} />
              </div>
              <h4 className="font-black text-gray-900 mb-2 uppercase text-sm">{title}</h4>
              <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);
};

/* ═══════════════════════════════════════════════
   EXPEDITION 2 — TRANSGLOBAL CAR EXPEDITION
═══════════════════════════════════════════════ */
const TransglobalExpedition = () => {
  const { language } = useLanguage();
  const fr = language === 'fr';
  return (
  <div>
    {/* Hero */}
    <div className="relative overflow-hidden" style={{ height: "calc(100vh - 300px)" }}>
      <img
        src="https://kelownacapnews.com/wp-content/uploads/sites/5/2025/01/250120-vne-arctic-expeditions.jpg?w=1024"
        alt="Total Composites habitats at the poles"
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => { e.target.src = 'https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/4mpc8h0w_image.png'; }}
      />
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0.88) 100%)' }} />
      <div className="relative z-10 flex flex-col justify-end h-full pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.2em' }}>
            {fr ? 'Expédition Transglobale en voiture — 2024–2025' : 'Transglobal Car Expedition — 2024–2025'}
          </p>
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-none mb-5 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? <>Premier<br /><span style={{ color: '#E8520A' }}>pôle à pôle</span><br />en voiture.</> : <>First Ever<br /><span style={{ color: '#E8520A' }}>Pole to Pole</span><br />By Car.</>}
          </h2>
          <p className="text-gray-300 text-lg max-w-xl leading-relaxed">
            {fr ? '475 jours. 92 000+ km. 42 pays. Du Pôle Nord au Pôle Sud — et retour. Les habitats TOTAL COMPOSITES l\'ont rendu possible.' : '475 days. 92,000+ km. 42 countries. North Pole to South Pole — and back. TOTAL COMPOSITES habitats made it possible.'}
          </p>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <ChevronDown size={28} className="text-white opacity-60" />
      </div>
    </div>

    {/* Stats */}
    <div className="bg-black py-10">
      <div className="max-w-6xl mx-auto px-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6">
        {[
          { value: '475', label: fr ? 'Jours' : 'Days' },
          { value: '92 000+', label: fr ? 'Kilomètres' : 'Kilometres' },
          { value: '42', label: fr ? 'Pays' : 'Countries' },
          { value: '8', label: fr ? 'Explorateurs' : 'Explorers' },
          { value: '-50°C', label: fr ? 'Temp. min.' : 'Min. Temp' },
          { value: '0', label: fr ? 'Tentatives précédentes' : 'Previous Attempts' },
        ].map((s, i) => (
          <div key={label} className="text-center">
            <div className="text-xl font-black text-white mb-0.5" style={{ color: i === 5 ? '#E8520A' : 'white' }}>{s.value}</div>
            <div className="text-xs text-gray-500 uppercase tracking-widest">{s.label}</div>
          </div>
        ))}
      </div>
    </div>

    {/* The Mission */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>{fr ? 'La mission' : 'The Mission'}</p>
            <h3 className="text-4xl font-black text-gray-900 mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
              {fr ? <>Une circumnavigation<br />terrestre complète<br />de la Terre</> : <>A Complete Surface<br />Circumnavigation<br />of the Earth</>}
            </h3>
            <div className="space-y-4 text-gray-600 text-sm leading-relaxed mb-8">
              {fr ? (
                <>
                  <p>L&apos;<strong className="text-gray-900">Expédition Transglobale en voiture</strong> est partie le 10 janvier 2024 avec un objectif que personne n&apos;avait jamais atteint — faire le tour de la Terre en voiture en traversant les deux pôles.</p>
                  <p>Le parcours couvrait plus de 92 000 km à travers 42 pays, de l&apos;océan Arctique gelé au plateau antarctique, en passant par l&apos;Afrique, l&apos;Europe, l&apos;Asie et les Amériques. Les températures extrêmes allaient de +50 °C dans le désert à -50 °C aux pôles.</p>
                  <p>Le 10 janvier 2025 — exactement un an après le départ — l&apos;équipe a atteint le <strong className="text-gray-900">Pôle Sud géographique</strong>. L&apos;expédition s&apos;est conclue les 28-29 avril 2025, accomplissant la première circumnavigation pôle à pôle en véhicule.</p>
                </>
              ) : (
                <>
                  <p>The <strong className="text-gray-900">Transglobal Car Expedition</strong> set out on January 10, 2024 with a goal no one had ever achieved — circumnavigate the entire Earth by car, crossing both the North and South poles.</p>
                  <p>The route covered over 92,000 km across 42 countries, from the frozen Arctic Ocean to the Antarctic Plateau, through Africa, Europe, Asia, and the Americas. Temperature extremes ranged from +50°C in the desert to -50°C at the poles.</p>
                  <p>On January 10, 2025 — exactly one year after departure — the team reached the <strong className="text-gray-900">geographical South Pole</strong>. The expedition concluded on April 28–29, 2025, completing the first-ever pole-to-pole circumnavigation by vehicle.</p>
                </>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: fr ? 'Départ' : 'Departed', value: fr ? '10 jan. 2024' : 'Jan 10, 2024' },
                { label: fr ? 'Pôle Sud' : 'South Pole', value: fr ? '10 jan. 2025' : 'Jan 10, 2025' },
                { label: fr ? 'Terminé' : 'Completed', value: fr ? '28 avr. 2025' : 'Apr 28, 2025' },
                { label: fr ? 'Traversée arctique' : 'Arctic Crossing', value: '6 000 km' },
                { label: fr ? 'Traversée antarctique' : 'Antarctic Crossing', value: '7 200 km' },
                { label: fr ? 'Record' : 'Record', value: fr ? 'Première mondiale' : 'World First' },
              ].map((s, i) => (
                <div key={s.label} className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-400 uppercase tracking-widest font-bold mb-0.5">{s.label}</p>
                  <p className="text-sm font-bold text-gray-900">{s.value}</p>
                </div>
              ))}
            </div>
          </div>

            <div className="space-y-4">
            <div className="bg-black rounded-2xl p-6">
              <h4 className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A' }}>{fr ? 'Itinéraire de l\'expédition' : 'Expedition Route'}</h4>
              <div className="space-y-3">
                {[
                  { phase: fr ? 'Traversée arctique' : 'Arctic Crossing', detail: fr ? '6 000 km sur l\'océan gelé' : '6,000 km over frozen ocean' },
                  { phase: fr ? 'Amérique du Nord' : 'North America', detail: 'Cheyenne → Yellowknife → Arctic' },
                  { phase: fr ? 'Europe et Asie' : 'Europe & Asia', detail: fr ? 'Plusieurs pays, terrain extrême' : 'Multiple countries, extreme terrain' },
                  { phase: fr ? 'Afrique' : 'Africa', detail: fr ? 'Routes sahariennes et subsahariennes' : 'Sahara and sub-Saharan routes' },
                  { phase: fr ? 'Traversée antarctique' : 'Antarctic Crossing', detail: fr ? '7 200 km à travers le plateau de glace' : '7,200 km across the ice plateau' },
                  { phase: fr ? 'Pôle Sud atteint' : 'South Pole Reached', detail: fr ? '10 janvier 2025' : 'January 10, 2025' },
                  { phase: fr ? 'Amérique du Sud → Amérique du Nord' : 'South America → North America', detail: fr ? 'Dernière étape du retour' : 'Final homeward leg' },
                  { phase: fr ? 'Expédition terminée' : 'Expedition Complete', detail: fr ? '28-29 avril 2025' : 'April 28–29, 2025' },
                ].map((step, i) => (
                  <div key={step.phase} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: '#E8520A' }} />
                    <div>
                      <p className="text-white text-sm font-semibold">{step.phase}</p>
                      <p className="text-gray-500 text-xs">{step.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Route Map */}
            <ZoomImg
              src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/1wjt9ibh_image.png"
              alt="Transglobal Car Expedition world route map"
              className="rounded-2xl shadow-lg w-full bg-white"
            />
            <a href="https://transglobalcar.com/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs font-semibold transition-colors hover:opacity-80 justify-end"
              style={{ color: '#E8520A' }}>
              TRANSGLOBALCAR.COM <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* TC Role */}
    <section className="py-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>Esquimalt, C.-B. → {fr ? 'Pôle Sud' : 'South Pole'}</p>
            <h3 className="text-4xl font-black text-white mb-6 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
              {fr ? <>Un morceau de Victoria<br />au bout<br />du monde</> : <>A Piece of Victoria<br />at the Bottom<br />of the World</>}
            </h3>
            <div className="space-y-4 text-gray-400 text-sm leading-relaxed mb-8">
              {fr ? (
                <>
                  <p>Après le succès de l&apos;expédition au Pôle Nord magnétique de 2023, Arctic Trucks a de nouveau fait appel à TOTAL COMPOSITES — cette fois pour l&apos;expédition la plus ambitieuse de l&apos;histoire automobile.</p>
                  <p>Le fondateur de TOTAL COMPOSITES basé à Esquimalt, <strong className="text-white">Andreas Schwall</strong>, a conçu les habitats utilisés par l&apos;équipe Transglobale tout au long de leur périple de 475 jours, de l&apos;océan Arctique au plateau antarctique.</p>
                  <p>Les habitats ont fonctionné parfaitement dans toute la gamme de conditions — de la chaleur désertique de +50 °C au froid polaire de -50 °C. Désormais, <strong className="text-white">les véhicules et les habitats sont maintenant basés en permanence au Pôle Sud pour de futures expéditions</strong> — un témoignage durable de l&apos;ingénierie composite canadienne aux confins de la Terre.</p>
                </>
              ) : (
                <>
                  <p>After the success of the 2023 Magnetic North Pole expedition, Arctic Trucks commissioned TOTAL COMPOSITES again — this time for the most ambitious expedition in automotive history.</p>
                  <p>TOTAL COMPOSITES' Esquimalt-based founder <strong className="text-white">Andreas Schwall</strong> designed the habitats used by the Transglobal team throughout their 475-day journey, from the Arctic Ocean to the Antarctic Plateau.</p>
                  <p>The habitats performed flawlessly across the full range of conditions — from +50°C desert heat to -50°C polar cold. Now, <strong className="text-white">the vehicles and habitats are now permanently based at the South Pole for future expeditions</strong> — a lasting testament to Canadian composite engineering at the ends of the Earth.</p>
                </>
              )}
            </div>
            <blockquote className="border-l-4 pl-5 py-1 text-gray-300 text-sm italic" style={{ borderColor: '#E8520A' }}>
              {fr
                ? '« C\'était un changement de paradigme. Ils ont dit : \u00abOn peut traîner à l\'intérieur. On peut préparer des repas à l\'intérieur… On veut refaire ça.\u00bb »'
                : '"This was a game-changer. They said, \'We can hang out inside. We can make meals inside… We want to do this again\'."'}
              <br /><span className="text-xs font-bold not-italic mt-2 block" style={{ color: '#E8520A' }}>— Andreas Schwall, {fr ? 'Fondateur' : 'Founder'}, TOTAL COMPOSITES</span>
            </blockquote>
          </div>
          <div className="space-y-4">
            <ZoomImg
              src="https://kelownacapnews.com/wp-content/uploads/sites/5/2025/01/250120-vne-andreas-schwall-camper.JPG?w=1024"
              alt="Andreas Schwall inside a Total Composites camper"
              className="rounded-2xl h-72 shadow-2xl"
            />
            <a href="https://kelownacapnews.com/2025/01/26/cold-comfort-bc-company-a-vital-part-of-treks-to-north-and-south-poles/"
              target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs font-semibold transition-colors hover:opacity-80 justify-end"
              style={{ color: '#E8520A' }}>
              {fr ? 'LIRE — KELOWNA CAPITAL NEWS' : 'READ — KELOWNA CAPITAL NEWS'} <ExternalLink size={11} />
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* Photo Gallery */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>{fr ? 'Sur le terrain' : 'In The Field'}</p>
        <div className="grid lg:grid-cols-3 gap-6 items-stretch">
          {/* Videos column — each exactly 1/2 gallery height */}
          <div className="flex flex-col gap-4" style={{ minHeight: 0 }}>
            <div className="flex-1 rounded-2xl overflow-hidden shadow-xl bg-black min-h-0">
              <iframe
                src="https://www.youtube.com/embed/USzkQp7gHXg"
                title="Transglobal Car Expedition — TOTAL COMPOSITES"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="flex-1 rounded-2xl overflow-hidden shadow-xl bg-black min-h-0">
              <iframe
                src="https://www.youtube.com/embed/KRkqXyQqLdo"
                title="Transglobal Car Expedition — TOTAL COMPOSITES 2"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
          {/* Photos grid */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-4">
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/quxbujmd_DSC05031.jpg.webp" alt="Transglobal Car Expedition team in front of Total Composites habitat" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/mtiicy2y_DJI_20250112203327_0.jpg.webp" alt="Aerial view of Transglobal base camp" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/ibg737g0_DSC04395.jpg.webp" alt="Total Composites habitat truck in polar snow" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/5c6unedb_DSC03136.jpg.webp" alt="Total Composites habitat in Antarctic blizzard" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/cb4obbw3_484611172_662423549707686_8908756371161574595_n.jpg" alt="Transglobal expedition field photo" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/8idnsjfz_image.png" alt="Transglobal Car Expedition polar conditions" className="rounded-2xl shadow-xl h-52" />
            <ZoomImg src="https://customer-assets.emergentagent.com/job_dashboard-114/artifacts/mtfz0mvz_image.png" alt="Total Composites habitat on the Transglobal expedition" className="rounded-2xl col-span-2 shadow-xl h-52" />
          </div>
        </div>
      </div>
    </section>

    {/* Legacy */}
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>{fr ? 'Héritage permanent' : 'Permanent Legacy'}</p>
        <h3 className="text-4xl font-black text-gray-900 mb-6 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
          {fr ? <>Laissé au Pôle Sud.<br /><span style={{ color: '#E8520A' }}>Pour toujours.</span></> : <>Left at the South Pole.<br /><span style={{ color: '#E8520A' }}>Forever.</span></>}
        </h3>
        <p className="text-gray-500 text-sm leading-relaxed max-w-2xl mx-auto mb-10">
          {fr
            ? 'Lorsque l\'Expédition Transglobale en voiture s\'est conclue, les véhicules — et leurs habitats TOTAL COMPOSITES — ont été laissés au Pôle Sud de façon permanente. Une structure conçue à Victoria, C.-B. se trouve maintenant au point le plus au sud de la Terre, comme un témoignage permanent de ce que la construction composite peut accomplir.'
            : 'When the Transglobal Car Expedition concluded, the vehicles — and their TOTAL COMPOSITES habitats — were left at the South Pole permanently. A Victoria, BC-engineered structure now stands at the southernmost point on Earth, as a permanent record of what composite construction can achieve.'}
        </p>
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { icon: Globe, title: fr ? 'Première mondiale' : 'World First', desc: fr ? 'Première circumnavigation terrestre complète de la Terre, pôle à pôle, en véhicule' : 'First complete surface circumnavigation of Earth, pole to pole, by vehicle' },
            { icon: Navigation, title: fr ? 'Les deux pôles' : 'Both Poles', desc: fr ? 'Les habitats TOTAL COMPOSITES sont les premiers campeurs jamais utilisés aux pôles Nord et Sud' : 'TOTAL COMPOSITES habitats are the first campers ever used at both North and South poles' },
            { icon: MapPin, title: fr ? 'Témoignage permanent' : 'Permanent Record', desc: fr ? 'Les habitats sont maintenant basés en permanence au Pôle Sud pour de futures expéditions' : 'The habitats are now permanently based at the South Pole for future expeditions' },
          ].map(({ icon: Icon, title, desc }, i) => (
            <div key={title} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 mx-auto" style={{ backgroundColor: '#FFF0E8' }}>
                <Icon size={18} style={{ color: '#E8520A' }} />
              </div>
              <h4 className="font-black text-gray-900 mb-2 uppercase text-sm">{title}</h4>
              <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  </div>
);
};

/* ═══════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════ */
const TABS = [
  { id: 'north-pole', label_en: 'Magnetic North Pole 2023', label_fr: 'Pôle Nord magnétique 2023' },
  { id: 'transglobal', label_en: 'Transglobal Expedition 2024–2025', label_fr: 'Expédition Transglobale 2024–2025' },
];

const Expedition = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    return tab === 'transglobal' ? 'transglobal' : 'north-pole';
  });

  return (
    <div className="min-h-screen pt-[76px]" style={{ backgroundColor: '#000' }}>
      <SEO
        title="Expeditions"
        description="Follow Total Composites expedition campers on real-world adventures — Arctic crossings, Transglobal expeditions, and extreme overlanding journeys across the globe."
        path="/expedition"
        keywords="expedition truck camper adventures, Transglobal Car Expedition, Arctic overlanding, Total Composites expeditions, extreme camping, South Pole expedition vehicle"
        jsonLd={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Expeditions', path: '/expedition' }
        ])}
      />
      {/* Page header */}
      <div className="bg-black py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#E8520A', letterSpacing: '0.15em' }}>{fr ? 'Éprouvé sur le terrain' : 'Field Proven'}</p>
          <h1 className="text-4xl lg:text-5xl font-black text-white uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Expéditions' : 'Expeditions'}
          </h1>
          <p className="text-gray-400 mt-3 max-w-2xl text-sm leading-relaxed">
            {fr
              ? "Preuve concrète. Les habitats TOTAL COMPOSITES ont été testés aux deux pôles — dans des conditions qui détruisent les constructions conventionnelles. Ce ne sont pas des histoires marketing. Ce sont des expéditions documentées."
              : 'Real-world proof. TOTAL COMPOSITES habitats have been tested at both poles — in conditions that destroy conventional builds. These are not marketing stories. These are documented expeditions.'}
          </p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 px-6 py-4 text-sm font-semibold border-b-2 transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id ? 'text-orange-600' : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
                style={activeTab === tab.id ? { borderBottomColor: '#E8520A', color: '#E8520A' } : {}}
              >
                {fr ? tab.label_fr : tab.label_en}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'north-pole' ? <NorthPole2023 /> : <TransglobalExpedition />}

      {/* CTA */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-black text-gray-900 mb-4 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Conçu pour votre expédition' : 'Built for Your Expedition'}
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-8">
            {fr
              ? "Le même système composite qui a atteint les deux pôles est disponible pour votre projet — des campeurs portés aux habitacles d’expédition entièrement personnalisés."
              : 'The same composite system that reached both poles is available for your build — from truck campers to fully custom expedition enclosures.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products?tab=pre-assembled" className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 hover:opacity-90 hover:scale-105" style={{ backgroundColor: '#E8520A' }}>
              {fr ? 'VOIR LES CAMPEURS PORTÉS' : 'VIEW CAMPERS'} <ArrowRight size={15} />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 font-bold text-gray-800 rounded-full text-sm border-2 border-gray-300 hover:border-gray-600 transition-all">
              {fr ? 'NOUS CONTACTER' : 'GET IN TOUCH'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Expedition;
